import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useIdleDetector } from '../hooks/useIdleDetector';
import { useAuth } from '../hooks/useAuth';
import { useSip } from '../hooks/useSip';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { Screensaver, type ScreensaverPhoto } from './Screensaver';
import type { ScreensaverMode } from '../hooks/useDisplaySettings';
import { onAttentionRequest } from '../services/AttentionBridge';
import { useEmergencyActive } from '../hooks/useEmergencyActive';

const SCREENSAVER_TIMEOUT = 180_000; // 3 minutes
const REFRESH_INTERVAL = 15 * 60 * 1000; // Refresh photo list every 15 minutes

/**
 * ScreensaverController — manages idle detection, photo fetching, and screensaver lifecycle.
 * Renders a photo screensaver when idle for 3 minutes and photos are available.
 * Falls back to clock-only mode when photos mode is selected but no photos exist.
 */
export function ScreensaverController() {
  const { api, isAuthenticated } = useAuth();
  const { currentCall } = useSip();
  const { settings, setSleep } = useDisplaySettings();
  const hasActiveCall = currentCall !== null;
  // Tijdens een noodoproep geen screensaver: niet starten, en een lopende meteen weg.
  const emergency = useEmergencyActive();

  // Disable idle detection during active calls, in sleep mode, or when the
  // screensaver is disabled for this location (portal display default).
  const { isIdle, wake } = useIdleDetector(
    SCREENSAVER_TIMEOUT,
    !hasActiveCall && !emergency && !settings.sleepMode && settings.screensaverEnabled,
  );

  const [photos, setPhotos] = useState<ScreensaverPhoto[]>([]);
  const [nextEvent, setNextEvent] = useState<{ title: string; startsAt: string; locationName?: string } | null>(null);
  const refreshRef = useRef<ReturnType<typeof setInterval>>();

  // Fetch screensaver photos
  const fetchPhotos = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const result = await api.get<{ success: boolean; data: ScreensaverPhoto[] }>('/api/v1/user/photos/screensaver');
      console.log('[Screensaver] Raw API response type:', typeof result, 'keys:', result ? Object.keys(result) : 'null');

      // Handle both wrapped { success, data } and direct array responses
      let photoList: ScreensaverPhoto[] = [];
      if (result?.success && Array.isArray(result.data)) {
        photoList = result.data;
      } else if (Array.isArray(result)) {
        // In case api.get() or a middleware unwraps the envelope
        photoList = result as unknown as ScreensaverPhoto[];
      } else if (result?.data && Array.isArray(result.data)) {
        photoList = result.data;
      } else {
        console.warn('[Screensaver] Unexpected response format:', JSON.stringify(result)?.substring(0, 200));
      }

      console.log('[Screensaver] Fetched', photoList.length, 'photos');
      if (photoList.length > 0) {
        console.log('[Screensaver] First photo URL:', photoList[0].url?.substring(0, 120));
        console.log('[Screensaver] First photo keys:', Object.keys(photoList[0]));
      }
      setPhotos(photoList);
    } catch (err) {
      console.warn('[Screensaver] Failed to fetch photos:', err);
    }
  }, [api, isAuthenticated]);

  // Fetch next upcoming event (for display on screensaver)
  const fetchNextEvent = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const result = await api.get<{ success: boolean; data: any[] }>('/api/v1/user/agenda/today');
      if (result?.success && result.data) {
        const now = new Date();
        const upcoming = result.data.find((e: any) => new Date(e.startsAt) > now);
        if (upcoming) {
          setNextEvent({
            title: upcoming.title,
            startsAt: upcoming.startsAt,
            locationName: upcoming.locationName
          });
        } else {
          setNextEvent(null);
        }
      }
    } catch {
      // Agenda endpoint may not exist yet — fail silently
    }
  }, [api, isAuthenticated]);

  // Initial fetch + periodic refresh
  useEffect(() => {
    fetchPhotos();
    fetchNextEvent();

    refreshRef.current = setInterval(() => {
      fetchPhotos();
      fetchNextEvent();
    }, REFRESH_INTERVAL);

    return () => {
      if (refreshRef.current) clearInterval(refreshRef.current);
    };
  }, [fetchPhotos, fetchNextEvent]);

  // Handle reaction
  const handleReaction = useCallback(async (photoId: number, reaction: 'liked' | 'disliked') => {
    try {
      await api.post(`/api/v1/user/photos/${photoId}/reaction`, { reaction });
      // Update local state optimistically
      setPhotos((prev) =>
        prev
          .map((p) => (p.id === photoId ? { ...p, reaction } : p))
          .filter((p) => p.reaction !== 'disliked') // Remove disliked from screensaver
      );
    } catch (err) {
      console.warn('[Screensaver] Failed to submit reaction:', err);
    }
  }, [api]);

  // ── Een melding vraagt het scherm (plan §6.5, T-2.07) ───────────────────
  //
  // Eén richting: de meldingstore stuurt een verzoek, deze controller beslist. De
  // meldingcomponenten kennen de screensaver NIET — anders zou de screensaver op zijn beurt
  // gaan meebepalen welke melding mag verschijnen, en dan zitten twee systemen elkaar in de
  // weg op het moment dat het ertoe doet.
  //
  // ⚠ `wakeFromSleep` is uitsluitend `urgent` (OQ-10). `high` breekt de screensaver maar
  // NIET de slaapstand: een tablet die 's nachts wakker wordt van een deurcontact is een
  // klacht in wording, en die keuze moet een automation-auteur bewust maken.
  //
  // Dimmen en de screensaver visueel doorbreken hoeft hier niet: elke meldingslaag zit in
  // constants/zIndex.ts boven de stand-byband. Wat dit toevoegt is dat de screensaver
  // daadwerkelijk WEGGAAT, zodat de bewoner na het wegklikken niet meteen weer tegen een
  // fotoscherm aankijkt.
  useEffect(() => onAttentionRequest((request) => {
    if (request.breakScreensaver) wake();
    if (request.wakeFromSleep) setSleep(false);
  }), [wake, setSleep]);

  useEffect(() => {
    if (emergency && isIdle) wake();
  }, [emergency, isIdle, wake]);

  // Wake up if a call comes in
  useEffect(() => {
    if (hasActiveCall) {
      if (isIdle) wake();
      if (settings.sleepMode) setSleep(false);
    }
  }, [hasActiveCall, isIdle, settings.sleepMode, wake, setSleep]);

  const screensaverMode = settings.screensaverMode as ScreensaverMode;

  // Log state for debugging
  useEffect(() => {
    console.log('[ScreensaverController] state:', {
      isIdle,
      hasActiveCall,
      screensaverMode,
      photosCount: photos.length,
      sleepMode: settings.sleepMode,
      firstPhotoUrl: photos[0]?.url?.substring(0, 60),
    });
  }, [isIdle, photos.length, screensaverMode, hasActiveCall, settings.sleepMode, photos]);

  // Don't render if not idle, call active, or screensaver disabled for this
  // location (portal display default).
  if (!isIdle || hasActiveCall || emergency || !settings.screensaverEnabled) {
    return null;
  }

  // No resident photos → the bright clock-only face. It used to fall back to the
  // stock background photo, but residents couldn't tell that screen apart from a
  // powered-off tablet; the clock face is now lit and legible on its own.
  const effectivePhotos: ScreensaverPhoto[] = photos;

  const effectiveMode: ScreensaverMode =
    effectivePhotos.length === 0 ? 'clock-only' : screensaverMode;

  console.log('[ScreensaverController] rendering Screensaver, mode:', effectiveMode, 'photos:', effectivePhotos.length);

  return (
    <Screensaver
      photos={effectivePhotos}
      onWake={wake}
      onReaction={handleReaction}
      nextEvent={nextEvent}
      mode={effectiveMode}
    />
  );
}
